import { faBell } from "@fortawesome/duotone-light-svg-icons"
import {
  faCheckCircle,
  faClockRotateLeft,
  faRotate,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Button,
  getRepetitionArrayFromRrule,
  JoonColorExpanded,
  JoonUIColor,
  ModalHeader,
  QuestInstance,
  TextButton,
  Typography,
  UserRole,
} from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useMemo } from "react"
import { useNavigate } from "react-router-dom"

import { useDeleteQuestModalStore } from "./DeleteQuestModal"
import ImageModal from "./ImageModal"
import { QuestModalView, useQuestModalStore } from "./useQuestModalStore"
import { UserQuestStatus } from "./UserQuestStatus"
import AttachmentItem from "../../../components/attachments/AttachmentItem"
import { useSBStore } from "../../../components/sendbird/useSBStore"
import { completeQuestStatuses } from "../../../constants"
import { useFamilyQuery } from "../../../networking/families/queries"
import {
  getQuestCategoryIconAndText,
  getRepetitionIntervalFromRrule,
  getRepetitionText,
} from "../../../util/quests"
import { ClientTab } from "../ClientTabNav"
import useCurrentClient from "../hooks/useCurrentClient"

const QuestDetails = () => {
  const {
    onClose,
    title,
    description,
    date,
    time,
    questGroup,
    setView,
    setQuestGroup,
    onOpen: openQuestModal,
  } = useQuestModalStore()
  const { data: family } = useFamilyQuery()
  const { onOpen: openDeleteModal } = useDeleteQuestModalStore()
  const { onClose: closeQuestModal } = useQuestModalStore()
  const navigate = useNavigate()

  const { currentCoachingGroup } = useCurrentClient()
  const { setInputText } = useSBStore()

  const userProfiles = useMemo(() => {
    const questUserIds = questGroup?.map((quest) => quest.user_id)
    return family?.profiles.filter((profile) =>
      questUserIds?.includes(profile.user.id)
    )
  }, [family, questGroup])

  const isParentCreated =
    questGroup?.[0]?.series.assigner_profile?.role !== UserRole.COACH
  const isAllCompleted = questGroup?.every((quest) =>
    completeQuestStatuses.includes(quest.status)
  )

  const handleSendReminder = () => {
    navigate(`/${currentCoachingGroup?.id}/${ClientTab.MESSAGES}`)
    setInputText(
      `Reminder: ${questGroup?.[0]?.series.title}
----

`
    )
    closeQuestModal()
  }

  const handleEditQuest = () => {
    setView(QuestModalView.EDIT)
  }

  const handleDuplicateQuest = () => {
    if (!questGroup) return
    openQuestModal(questGroup)
    setView(QuestModalView.ADD)
    setQuestGroup(null)
  }

  if (!questGroup?.[0]) return null

  return (
    <>
      <ModalHeader title="Task details" onClose={onClose} showBorderBottom />
      <div className="flex flex-col gap-4 p-6 pt-4">
        <div className="flex flex-col gap-2">
          <Typography variant="h3">{title}</Typography>
          <Typography variant="body">{description}</Typography>
          {questGroup?.[0]?.series.resource && (
            <AttachmentItem
              name={questGroup?.[0]?.series.resource.title}
              url={questGroup?.[0]?.series.resource.resource_url}
            />
          )}
        </div>
        {isParentCreated && (
          <ParentQuestDetailsSection questInstance={questGroup?.[0]} />
        )}
        <div className="flex flex-col gap-2">
          <Typography variant="bodyBold">Details and progress</Typography>
          <div className="flex flex-row gap-2 px-4 py-3 border border-borderPrimary rounded-md items-center">
            <div className="min-w-6 min-h-6 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faBell}
                style={{
                  // @ts-ignore
                  "--fa-primary-color": JoonUIColor.icon.accent,
                  "--fa-secondary-color": JoonColorExpanded.indigo300,
                }}
              />
            </div>
            <Typography variant="body">
              Scheduled for{" "}
              {date ? dayjs(date).format("MMM D, YYYY") : "No date selected"}
              {time && ` at ${time}`}
            </Typography>
          </div>
          {userProfiles?.map((profile) => (
            <UserQuestStatus
              key={profile.user.id}
              profile={profile}
              questGroup={questGroup}
            />
          ))}
        </div>
        {!isParentCreated && (
          <div className="flex flex-row w-full gap-4 justify-between items-center">
            <TextButton onClick={openDeleteModal}>
              <Typography
                variant="body"
                color={JoonUIColor.semantic.destructive}
              >
                Delete
              </Typography>
            </TextButton>
            {isAllCompleted ? (
              <div className="flex justify-end">
                <Button
                  text="Duplicate and assign again"
                  onClick={handleDuplicateQuest}
                />
              </div>
            ) : (
              <div className="flex flex-row gap-4 w-full justify-end">
                <Button
                  buttonType="secondary"
                  text="Edit task"
                  onClick={handleEditQuest}
                />
                <Button
                  text="Send reminder"
                  onClick={handleSendReminder}
                  style={{ minWidth: "200px" }}
                />
              </div>
            )}
          </div>
        )}
      </div>
      <ImageModal />
    </>
  )
}

export default QuestDetails

const ParentQuestDetailsSection = ({
  questInstance,
}: {
  questInstance: QuestInstance
}) => {
  const questSeries = questInstance.series
  const byWeekday = getRepetitionArrayFromRrule(questSeries?.recurrence || "")
  const interval = getRepetitionIntervalFromRrule(questSeries?.recurrence || "")
  const recurrence = getRepetitionText(byWeekday, interval)
  const reqReview = questSeries?.requires_review
  const reqPhoto = questSeries?.requires_completion_photo
  const reqNotes = questSeries?.requires_completion_notes
  const requiresReviewText = reqReview
    ? `Review${reqPhoto ? " with photo" : ""}${
        reqNotes ? (reqPhoto ? " and notes" : " with notes") : ""
      } required`
    : "No completion requirement"

  return (
    <div className="flex flex-col items-center w-full  border border-borderPrimary rounded-md">
      {questSeries.mandatory && (
        <div className="flex flex-row items-center gap-2 w-full px-4 py-3">
          <FontAwesomeIcon
            icon={faRotate}
            fontSize="14px"
            className="max-w-5 min-w-5"
            color={JoonUIColor.icon.accent}
          />
          <Typography variant="bodySmall">
            {questSeries.mandatory ? "Mandatory task" : "Optional task"}
          </Typography>
        </div>
      )}
      {questSeries.skill && (
        <div
          className={`flex flex-row items-center gap-2 w-full px-4 py-3 ${
            questSeries.mandatory ? "border-t border-borderPrimary" : ""
          }`}
        >
          <FontAwesomeIcon
            icon={getQuestCategoryIconAndText(questSeries.skill).icon}
            color={JoonUIColor.icon.accent}
            fontSize="14px"
            className="max-w-5 min-w-5"
          />
          <Typography variant="bodySmall">
            {getQuestCategoryIconAndText(questSeries.skill).text}
          </Typography>
        </div>
      )}
      <div className="flex flex-row items-center gap-2 w-full px-4 py-3 border-t border-borderPrimary">
        <FontAwesomeIcon
          icon={faRotate}
          fontSize="14px"
          color={JoonUIColor.icon.accent}
          className="max-w-5 min-w-5"
        />
        <Typography variant="bodySmall">{recurrence}</Typography>
      </div>
      {questSeries.timer_length && (
        <div className="flex flex-row items-center gap-2 w-full px-4 py-3 border-t border-borderPrimary">
          <FontAwesomeIcon
            icon={faClockRotateLeft}
            fontSize="14px"
            color={JoonUIColor.icon.accent}
            className="max-w-5 min-w-5"
          />
          <Typography variant="bodySmall">
            {questSeries.timer_length
              ? `${questSeries.timer_length.slice(
                  3,
                  5
                )}m ${questSeries.timer_length.slice(6, 8)}s timer`
              : "No timer"}
          </Typography>
        </div>
      )}
      <div className="flex flex-row items-center gap-2 w-full px-4 py-3 border-t border-borderPrimary">
        <FontAwesomeIcon
          icon={faCheckCircle}
          fontSize="14px"
          color={JoonUIColor.icon.accent}
          className="max-w-5 min-w-5"
        />
        <Typography variant="bodySmall">{requiresReviewText}</Typography>
      </div>
    </div>
  )
}
